import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import { useFounder } from "@/lib/founder/useFounder";

/**
 * Wraps protected routes. Requires:
 *  1. signed in (else /auth)
 *  2. onboarding_completed_at set (else /onboarding)
 *
 * Founders bypass the onboarding redirect entirely.
 * The /onboarding route itself uses ProtectedRoute only, not this gate.
 */
const OnboardingGate = ({ children }: { children: React.ReactNode }) => {
  const { session, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { isFounder, loading: founderLoading } = useFounder();
  const location = useLocation();

  if (
    authLoading ||
    (session && profileLoading) ||
    (session && founderLoading)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!session) {
    return <Navigate to={`/auth?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Founders and partner-code users skip the onboarding requirement.
  const isPartner = profile?.kyc_provider === "partner_code";
  if (profile && !profile.onboarding_completed_at && !isFounder && !isPartner) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default OnboardingGate;
